import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HelpHint } from "@/components/HelpHint";
import { ShieldAlert, ShieldCheck } from "lucide-react";

// Schedule -> "Schedule a Lesson" create dialog, including the inline
// payment-hold override flow. Extracted from Schedule.js (props-driven, no
// behavior change).
export const CreateLessonDialog = ({
  open,
  onOpenChange,
  paymentHold,
  overrideNote,
  onOverrideNoteChange,
  overrideSubmitting,
  onGrantOverrideAndRetry,
  form,
  onFormChange,
  students,
  instructors,
  vehicles,
  businessRules,
  activeLocations,
  onCreateAppointment,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg" data-testid="create-lesson-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-1.5">
          Schedule a Lesson
          <HelpHint text="If the student's account is past due, scheduling is automatically put on hold — you can grant a one-time courtesy or full override right here." testId="create-lesson-help" />
        </DialogTitle>
      </DialogHeader>

      {paymentHold && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 space-y-3" data-testid="payment-hold-banner">
          <div className="flex items-start gap-2">
            <ShieldAlert className="h-4 w-4 text-rose-700 shrink-0 mt-0.5" />
            <p className="text-sm text-rose-800">{paymentHold.message}</p>
          </div>
          <Input placeholder="Optional note (e.g. reason for override)" value={overrideNote} onChange={(e) => onOverrideNoteChange(e.target.value)} data-testid="override-note-input" />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" disabled={overrideSubmitting} onClick={() => onGrantOverrideAndRetry("courtesy")} data-testid="grant-courtesy-override-button">
              <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Courtesy Override (1 booking)
            </Button>
            <Button size="sm" disabled={overrideSubmitting} onClick={() => onGrantOverrideAndRetry("full")} className="bg-[hsl(var(--role-accent))] hover:opacity-95" data-testid="grant-full-override-button">
              <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Full Override
            </Button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Type</Label>
          <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={form.type} onChange={(e) => onFormChange((f) => ({ ...f, type: e.target.value }))} data-testid="create-lesson-type-select">
            <option value="lesson">Driving Lesson</option>
          </select>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Student</Label>
          <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={form.student_id} onChange={(e) => onFormChange((f) => ({ ...f, student_id: e.target.value }))} data-testid="create-lesson-student-select">
            <option value="">Select student</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>{s.full_name}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Instructor</Label>
          <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={form.instructor_id} onChange={(e) => onFormChange((f) => ({ ...f, instructor_id: e.target.value }))} data-testid="create-lesson-instructor-select">
            <option value="">Select instructor</option>
            {instructors.map((i) => (
              <option key={i.id} value={i.id}>{i.full_name}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Vehicle (optional)</Label>
          <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={form.vehicle_id} onChange={(e) => onFormChange((f) => ({ ...f, vehicle_id: e.target.value }))} data-testid="create-lesson-vehicle-select">
            <option value="">No vehicle</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>{v.make} {v.model} - {v.license_plate}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>Date</Label>
          <Input type="date" value={form.date} onChange={(e) => onFormChange((f) => ({ ...f, date: e.target.value }))} data-testid="create-lesson-date-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Start time</Label>
          <Input type="time" value={form.start_time} onChange={(e) => onFormChange((f) => ({ ...f, start_time: e.target.value }))} data-testid="create-lesson-start-input" />
        </div>
        <div className="space-y-1.5">
          <Label>End time</Label>
          <Input type="time" value={form.end_time} onChange={(e) => onFormChange((f) => ({ ...f, end_time: e.target.value }))} data-testid="create-lesson-end-input" />
        </div>
        {businessRules && (
          <p className="text-xs text-muted-foreground sm:col-span-2">
            Business hours: {businessRules.business_start_time}–{businessRules.business_end_time}, {(businessRules.business_days || []).join(", ")}.
          </p>
        )}
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Meeting location override (optional)</Label>
          <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={form.meeting_location} onChange={(e) => onFormChange((f) => ({ ...f, meeting_location: e.target.value }))} data-testid="create-lesson-meeting-location-select">
            <option value="">Use student&apos;s default location</option>
            {activeLocations.map((l) => (
              <option key={l.id} value={l.name}>{l.name}</option>
            ))}
          </select>
          {form.student_id && (
            <p className="text-xs text-muted-foreground">
              Default: {students.find((s) => s.id === form.student_id)?.default_meeting_location || "Not set"}. Selecting a different location will notify the student and parent.
            </p>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button onClick={onCreateAppointment} data-testid="create-lesson-submit-button">Schedule Lesson</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
